import { memo, useMemo } from 'react';
import { Alert, AlertTitle, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import DeploymentRow from '../../service-administration/components/DeploymentRow';
import { summarizeCell, cellHeadline, severityColor, formatAge } from '../landscapeStatus';
import { rowConfigFor } from '../../service-administration/rowConfig';
import { cellKey, type LandscapeMatrix } from '../hooks/useLandscapeMatrix';

interface SpokeViewProps {
  customerId: string;
  accountId: string;
  region: string;
  matrix: LandscapeMatrix;
  onOpenActivity: () => void;
}

const SpokeView = memo<SpokeViewProps>(({ customerId, accountId, region, matrix, onOpenActivity }) => {
  const deployments = useMemo(
    () => matrix.deploymentsByCell.get(cellKey(accountId, region)) ?? [],
    [matrix.deploymentsByCell, accountId, region],
  );
  const accountName = matrix.accounts.find((a) => a.awsAccountId === accountId)?.name ?? accountId;

  const productByName = useMemo(() => new Map(matrix.products.map((p) => [p.name, p])), [matrix.products]);
  const productById = useMemo(() => new Map(matrix.products.map((p) => [p.id, p])), [matrix.products]);

  const summary = useMemo(() => {
    const rows = deployments.map((d) =>
      rowConfigFor(d, productByName.get(d.product_name ?? '') ?? productById.get(d.product_id), matrix.dependencies[d.id]),
    );
    return summarizeCell(accountId, region, deployments, rows);
  }, [deployments, productByName, productById, matrix.dependencies, accountId, region]);

  const heartbeats = deployments.map((d) => d.last_heartbeat).filter((x): x is string => !!x).sort();
  const lastHeartbeat = heartbeats.length > 0 ? heartbeats[heartbeats.length - 1] : undefined;

  const color = severityColor(summary.severity);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{accountName}</Typography>
          <Chip size="small" variant="outlined" label={region} sx={{ fontFamily: 'monospace' }} />
          <Chip
            size="small"
            color={color === 'default' ? 'default' : color}
            variant={summary.severity === 'ok' ? 'filled' : 'outlined'}
            label={cellHeadline(summary)}
          />
          {summary.blocked > 0 && <Chip size="small" color="error" label={`${summary.blocked} blocked`} />}
          {summary.pendingSync > 0 && <Chip size="small" color="info" variant="outlined" label={`${summary.pendingSync} pending sync`} />}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              last heartbeat {formatAge(lastHeartbeat)}
            </Typography>
            <Button size="small" startIcon={<HistoryIcon fontSize="small" />} onClick={onOpenActivity}>
              Activity
            </Button>
          </Box>
        </Stack>
        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>{accountId}</Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
        {deployments.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No deployments in this account &amp; region.</Typography>
          </Box>
        ) : (
          deployments.map((d) => (
            <Box key={d.id}>
              {d.blocked && (
                <Alert severity="error" variant="outlined" sx={{ mb: 0.5 }}>
                  <AlertTitle sx={{ mb: 0.25 }}>
                    {d.product_name ?? d.product_id} — blocked{d.blocked_reason ? `: ${d.blocked_reason}` : ''}
                  </AlertTitle>
                  {d.actual_status && (
                    <Typography variant="caption" color="text.secondary">
                      AWS provisioned-product status: {d.actual_status}
                      {d.last_action_error ? ` · ${d.last_action_error}` : ''}
                    </Typography>
                  )}
                </Alert>
              )}
              {/* recentVersions is intentionally empty here: the Landscape spoke view is read-oriented.
                  Picking a prior version to roll back to stays in the Deployments modal. */}
              <DeploymentRow
                customerId={customerId}
                deployment={d}
                product={productByName.get(d.product_name ?? '') ?? productById.get(d.product_id)}
                dependencies={matrix.dependencies[d.id]}
                recentVersions={[]}
                onRefresh={matrix.refresh}
              />
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
});

SpokeView.displayName = 'SpokeView';
export default SpokeView;
